import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectAllAccounts, selectCurrentAccount, selectExternalAccounts } from "fond/api";
import { AccountAccess } from "fond/types";

/**
 * Hook to determine the user's access level for the current account.
 *
 * @returns "member" if the user is a member of the account,
 *          "external" if the user has access to shared items but is not a member,
 *          "none" if there is no current account or the user has no access
 */
export const useGetAccountAccess = (): AccountAccess => {
  const currentAccount = useSelector(selectCurrentAccount);
  const userAccounts = useSelector(selectAllAccounts);
  const externalAccounts = useSelector(selectExternalAccounts);

  return useMemo(() => {
    if (!currentAccount?.ID) {
      return "none";
    }

    const isMember = userAccounts.some((userAccount) => userAccount.ID === currentAccount.ID);
    if (isMember) return "member";

    const isExternal = externalAccounts.some((externalAccount) => externalAccount.ID === currentAccount.ID);
    if (isExternal) return "external";

    return "none";
  }, [currentAccount?.ID, externalAccounts, userAccounts]);
};
